import {EventHint, addBreadcrumb} from '@sentry/react';
import {FormikErrors, FormikTouched} from 'formik';
import {round} from 'lodash-es';
import {OptionsObject, SnackbarKey, SnackbarMessage} from 'notistack';
import React from 'react';
import {includesSaltus} from '../../../../utils/includesSaltus';
import {Pot, Pot_children} from '../../../../schema-types/Pot';
import OWNERSHIP from '../../../../constants/Ownership';
import {createBuilderUpdateProps} from '../../useSaveBuilder/useSaveBuilder';
import {BuilderPot} from '../../../../schema-types/BuilderPot';
import {shortenName} from '../../../../utils/shortenName';
import {builderFieldsType} from '../builderFields';
import {AccountBasicFragment} from '../../../../schema-types/AccountBasicFragment';
import {
  listErrorMessagesAsNodes,
  formatBuilderNestedKeys,
  getListOfErrors,
} from '../builderValidation';
import {SaltusAny} from '../../../../utils/SaltusAny';
import saltus from '../../../../shared/logger';

// ── Subplan state utilities (Option B per-feature components) ─────────────────

/** Single-select (radio) builders. Pass the currently selected id. */
export function getSubplanState(
  pot: Pot | Pot_children,
  subplans: Pot_children[],
  selectedId: string | undefined
) {
  const showSubPlans = !!(
    (pot as Pot).children?.length &&
    subplans.length &&
    includesSaltus((pot as Pot).productProvider?.name)
  );
  return {
    showSubPlans,
    isSelected: selectedId === pot.id,
    isPartiallySelected: showSubPlans && subplans.some((sp) => selectedId === sp.id),
  };
}

/** Transfer source (multi-select checkbox). Pass the transferSources map. */
export function getTransferSubplanState(
  pot: Pot,
  subplans: Pot_children[],
  sources: Record<string, {checked?: boolean} | undefined>
) {
  const showSubPlans = !!(
    pot.children?.length &&
    subplans.length &&
    includesSaltus(pot.productProvider?.name)
  );
  return {
    showSubPlans,
    isSelected: !!sources[pot.id]?.checked,
    isPartiallySelected: showSubPlans && subplans.some((sp) => !!sources[sp.id]?.checked),
  };
}

// ─────────────────────────────────────────────────────────────────────────────

export const findParentOfChild = (
  pots: BuilderPot[],
  childId: string
): BuilderPot | undefined =>
  pots.find((p) => p.dentistChildren?.some((c) => c.id === childId));

export const hasWrapper = (mainPlanType?: string): boolean => {
  if (!mainPlanType) {
    return false;
  }
  return mainPlanType === 'pension' || mainPlanType === 'bond';
};

export function optionalRound(val?: number | string, multiplier = 1) {
  if (val && typeof val === 'number') {
    return round(val * multiplier, 0);
  }
  try {
    const valNum = Number(val);
    if (Number.isNaN(valNum)) {
      return '';
    }
    return String(round(valNum * multiplier, 2));
  } catch (err) {
    // do nothing
  }
  if (val) {
    return val;
  }
  return '';
}

export const setContactnameFromGoals = ({
  values,
  account: accountContactsData,
  setContactName,
}: {
  values: builderFieldsType;
  // accountContactsData: GetGoalContacts | undefined;
  account: AccountBasicFragment | undefined;
  setContactName: React.Dispatch<React.SetStateAction<string>>;
}) => {
  if (!values.contact) {
    return;
  }
  if (!accountContactsData) {
    return;
  }

  if (accountContactsData.isTrust) {
    setContactName(`${accountContactsData.name}`);
    return;
  }

  if (values.contact === OWNERSHIP.JOINT_CODE && !accountContactsData?.secondaryContact) {
    return;
  }

  if (values.contact === OWNERSHIP.JOINT_CODE) {
    const primary = accountContactsData?.primaryContact?.simplifiedName;
    const secondary = accountContactsData?.secondaryContact?.simplifiedName;
    setContactName(`${primary} & ${secondary}`);
    return;
  }

  const contact =
    // eslint-disable-next-line no-nested-ternary
    accountContactsData.primaryContact?.id === values.contact
      ? accountContactsData.primaryContact
      : accountContactsData.secondaryContact?.id === values.contact
      ? accountContactsData.secondaryContact
      : accountContactsData.contacts?.find((x) => x?.id && x?.id === values.contact);
  // const contact = accountContactsData?.goalContacts.find((x) => x?.id === values.contact);
  setContactName(shortenName(contact?.simplifiedName || contact?.name || '') || '');
};

export const saveDraftBuilderFactory = ({
  setSaving,
  createUpdate,
  values,
  contactName,
  sources,
  destinations,
}: {
  setSaving: React.Dispatch<React.SetStateAction<boolean>>;
  createUpdate: (props: createBuilderUpdateProps) => Promise<any>;
  values: builderFieldsType;
  contactName: string;
  sources: BuilderPot[];
  destinations: BuilderPot[];
}) => {
  return () => {
    setSaving(true);
    saltus.log({'Saving draft values': values});
    createUpdate({
      values,
      contactName,
      sourcePots: sources,
      destinationPots: destinations,
    });
  };
};

export const finaliseBuilderFactory = ({
  createUpdate,
  values,
  contactName,
  sources,
  destinations,
  setSaving,
  closeSnackbar,
  validateForm,
  setNestedObjectValues,
  setTouched,
  enqueueSnackbar,
  closeWarning,
}: {
  setSaving: React.Dispatch<React.SetStateAction<boolean>>;
  closeWarning: () => void;
  createUpdate: (props: createBuilderUpdateProps) => Promise<any>;
  values: builderFieldsType;
  contactName: string;
  sources: BuilderPot[];
  destinations: BuilderPot[];
  closeSnackbar: (key?: SnackbarKey | undefined) => void;
  validateForm: (values?: SaltusAny) => Promise<FormikErrors<builderFieldsType>>;
  setNestedObjectValues: <T>(
    object: SaltusAny,
    value: SaltusAny,
    visited?: SaltusAny,
    response?: SaltusAny
  ) => T;
  setTouched: (
    touched: FormikTouched<builderFieldsType>,
    shouldValidate?: boolean | undefined
  ) => void;
  enqueueSnackbar: (
    message: SnackbarMessage,
    options?: OptionsObject | undefined
  ) => SnackbarKey;
}) => {
  return () => {
    addBreadcrumb({
      message: 'Started finalising instruction with form values',
      data: values,
    });
    setSaving(true);
    closeWarning();
    closeSnackbar();
    validateForm().then((errors) => {
      // set all touched
      const newtouched = setNestedObjectValues({...errors}, true);
      setTouched(newtouched as any);
      setTimeout(async () => {
        if (!errors || Object.keys(errors).length === 0) {
          saltus.log({'Finalising values': values});
          addBreadcrumb({
            message: 'Finalising validated instruction with form values',
            data: values,
          });
          await createUpdate({
            values,
            contactName,
            sourcePots: sources,
            destinationPots: destinations,
          });
          // never turn off saving if successful to keep it looking tidy
        } else {
          enqueueSnackbar(
            listErrorMessagesAsNodes(getListOfErrors(errors, formatBuilderNestedKeys)),
            {
              variant: 'error',
              autoHideDuration: null,
            }
          );
          addBreadcrumb({
            message: 'instruction validation failed',
            data: {values, errors},
          });

          setSaving(false);
        }
      });
    });
  };
};
